import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import { GraphQlService } from '@shared/services/graphql-service';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import {
  ADD_TERMINAL_DETAILS,
  ADD_EWURA_LICENSE,
  ADD_LGA_LICENSE,
  FETCH_EWURA_LICENCE,
  FETCH_LGA_LICENCE,
  GENERATE_TERMINAL_CONTROL_NUMBER,
  GET_TERMINAL_REGISTRATION_PROGRESS,
  INITIATE_TERMINAL_REGISTRATION,
  SUBMIT_TERMINAL_APPLICATION,
  REMOVE_TERMINAL_TANK,
} from '../graphql/terminal-registration.graphql';
import {
  AddEwuraLicenseResponse,
  AddLgaLicenseResponse,
  AddTerminalDetailsResponse,
  FetchEwuraLicenceResponse,
  FetchLgaLicenceResponse,
  GenerateTerminalControlNumberResponse,
  GetTerminalRegistrationProgressResponse,
  InitiateTerminalRegistrationResponse,
  RemoveTerminalTankResponse,
  SubmitTerminalApplicationResponse,
} from '../response/terminal-registration-initiate.response';
import * as TerminalRegistrationActions from './terminal-registration.action';

@Injectable({
  providedIn: 'root',
})
export class TerminalRegistrationEffects {
  constructor(
    private actions$: Actions,
    private graphQlService: GraphQlService,
  ) {}

  $initiateTerminalRegistration = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalRegistrationActions.TerminalInitiateActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<InitiateTerminalRegistrationResponse>({
            mutation: INITIATE_TERMINAL_REGISTRATION,
            variables: { input: request },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.initiateRegistration;
              if (!payload.status) {
                return TerminalRegistrationActions.TerminalInitiateActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Terminal registration initiation failed',
                    data: null,
                  },
                });
              }

              return TerminalRegistrationActions.TerminalInitiateActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                TerminalRegistrationActions.TerminalInitiateActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Terminal registration initiation failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getTerminalRegistrationProgress = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalRegistrationActions.TerminalProgressActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetTerminalRegistrationProgressResponse>({
            query: GET_TERMINAL_REGISTRATION_PROGRESS,
            variables: { terminalUuid: this.normalizeUuid(request) },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.getTerminalRegistrationProgress;
              if (!payload?.status) {
                return TerminalRegistrationActions.TerminalProgressActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to fetch terminal registration progress',
                    data: null,
                  },
                });
              }

              return TerminalRegistrationActions.TerminalProgressActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                TerminalRegistrationActions.TerminalProgressActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Failed to fetch terminal registration progress',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $addTerminalDetails = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalRegistrationActions.TerminalAddDetailsActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<AddTerminalDetailsResponse>({
            mutation: ADD_TERMINAL_DETAILS,
            variables: { input: request },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.addTerminalDetails;
              if (!payload?.status) {
                return TerminalRegistrationActions.TerminalAddDetailsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to add terminal details',
                    data: null,
                  },
                });
              }

              return TerminalRegistrationActions.TerminalAddDetailsActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                TerminalRegistrationActions.TerminalAddDetailsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Failed to add terminal details',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $fetchEwuraLicence = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalRegistrationActions.TerminalFetchEwuraLicenceActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<FetchEwuraLicenceResponse>({
            query: FETCH_EWURA_LICENCE,
            variables: { licenseNumber: request?.licenseNumber ?? null },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.fetchEwuraLicence;
              if (!payload?.status) {
                return TerminalRegistrationActions.TerminalFetchEwuraLicenceActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to fetch EWURA licence',
                    data: null,
                  },
                });
              }

              return TerminalRegistrationActions.TerminalFetchEwuraLicenceActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                TerminalRegistrationActions.TerminalFetchEwuraLicenceActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Failed to fetch EWURA licence',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $addEwuraLicense = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalRegistrationActions.TerminalAddEwuraLicenseActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<AddEwuraLicenseResponse>({
            mutation: ADD_EWURA_LICENSE,
            variables: { input: request },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.addEwuraLicense;
              if (!payload?.status) {
                return TerminalRegistrationActions.TerminalAddEwuraLicenseActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to add EWURA license',
                    data: null,
                  },
                });
              }

              return TerminalRegistrationActions.TerminalAddEwuraLicenseActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                TerminalRegistrationActions.TerminalAddEwuraLicenseActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Failed to add EWURA license',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $fetchLgaLicence = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalRegistrationActions.TerminalFetchLgaLicenceActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<FetchLgaLicenceResponse>({
            query: FETCH_LGA_LICENCE,
            variables: { licenseNumber: request?.licenseNumber ?? null },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.fetchLgaLicence;
              if (!payload?.status) {
                return TerminalRegistrationActions.TerminalFetchLgaLicenceActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to fetch LGA licence',
                    data: null,
                  },
                });
              }

              return TerminalRegistrationActions.TerminalFetchLgaLicenceActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                TerminalRegistrationActions.TerminalFetchLgaLicenceActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Failed to fetch LGA licence',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $addLgaLicense = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalRegistrationActions.TerminalAddLgaLicenseActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<AddLgaLicenseResponse>({
            mutation: ADD_LGA_LICENSE,
            variables: { input: request },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.addLgaLicense;
              if (!payload?.status) {
                return TerminalRegistrationActions.TerminalAddLgaLicenseActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to add LGA license',
                    data: null,
                  },
                });
              }

              return TerminalRegistrationActions.TerminalAddLgaLicenseActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                TerminalRegistrationActions.TerminalAddLgaLicenseActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Failed to add LGA license',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $submitTerminalApplication = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalRegistrationActions.TerminalSubmitApplicationActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SubmitTerminalApplicationResponse>({
            mutation: SUBMIT_TERMINAL_APPLICATION,
            variables: { input: request },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.submitTerminalApplication;
              if (!payload?.status) {
                return TerminalRegistrationActions.TerminalSubmitApplicationActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to submit terminal application',
                    data: payload ?? null,
                  },
                });
              }

              return TerminalRegistrationActions.TerminalSubmitApplicationActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                TerminalRegistrationActions.TerminalSubmitApplicationActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Failed to submit terminal application',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $generateControlNumber = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalRegistrationActions.TerminalGenerateControlNumberActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<GenerateTerminalControlNumberResponse>({
            mutation: GENERATE_TERMINAL_CONTROL_NUMBER,
            variables: { billUuid: request.billingUuid },
            apolloNamespace: ApolloNamespace.billing,
          })
          .pipe(
            map((response) => {
              const payload = response.data.generateControlNumber;

              if (!payload?.status) {
                return TerminalRegistrationActions.TerminalGenerateControlNumberActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to generate control number',
                    data: payload ?? null,
                  },
                });
              }

              return TerminalRegistrationActions.TerminalGenerateControlNumberActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload.data,
                },
              });
            }),
            catchError((error) =>
              of(
                TerminalRegistrationActions.TerminalGenerateControlNumberActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Failed to generate control number',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

   $removeTerminalTank = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalRegistrationActions.TerminalRemoveTerminalTankActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<RemoveTerminalTankResponse>({
            mutation: REMOVE_TERMINAL_TANK,
            variables: { input: request },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.removeTerminalTank;

              if (!payload?.status) {
                return TerminalRegistrationActions.TerminalRemoveTerminalTankActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to remove terminal tank',
                    data: payload ?? null,
                  },
                });
              }

              return TerminalRegistrationActions.TerminalRemoveTerminalTankActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload.data,
                },
              });
            }),
            catchError((error) =>
              of(
                TerminalRegistrationActions.TerminalRemoveTerminalTankActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Failed to remove terminal tank',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  private normalizeUuid(value?: string | null): string | null {
    if (!value) return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
}
